/* La Nuit — product-reviews.js
   - Toggles the submission form
   - Drives the 5-star rating picker (writes hidden input + a11y)
   - Sorts/filters the already-rendered review list client-side
   - Replaces "May 2025" date with relative ("2 weeks ago") when < 30 days
*/
(function () {
  'use strict';

  function init(root) {
    if (!root || root.__prInit) return;
    root.__prInit = true;

    initFormToggle(root);
    initLoginButtons(root);
    initRatingPicker(root);
    initSort(root);
    initRelativeDates(root);
    initPhotoSlots(root);
  }

  // ---------------- Photo upload slots ----------------
  function initPhotoSlots(root) {
    var slots = root.querySelectorAll('[data-photo-slot]');
    if (!slots.length) return;
    slots.forEach(function (slot) {
      var input = slot.querySelector('[data-photo-input]');
      var preview = slot.querySelector('[data-photo-preview]');
      var removeBtn = slot.querySelector('[data-photo-remove]');
      if (!input || !preview || !removeBtn) return;

      input.addEventListener('change', function () {
        var file = input.files && input.files[0];
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
          alert('Please keep images under 8 MB.');
          input.value = '';
          return;
        }
        var url = URL.createObjectURL(file);
        preview.innerHTML = '<img src="' + url + '" alt="">';
        preview.removeAttribute('hidden');
        removeBtn.removeAttribute('hidden');
        slot.setAttribute('data-filled', '');
      });

      removeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        input.value = '';
        preview.innerHTML = '';
        preview.setAttribute('hidden', '');
        removeBtn.setAttribute('hidden', '');
        slot.removeAttribute('data-filled');
      });
    });
  }

  // ---------------- Sign-in CTA ----------------
  // Rendered as a button carrying the login URL in a data attribute, so
  // the auth redirect is never a crawlable link on the product page.
  function initLoginButtons(root) {
    root.querySelectorAll('[data-login-href]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var href = btn.getAttribute('data-login-href');
        if (href) window.location.href = href;
      });
    });
  }

  // ---------------- Form toggle ----------------
  function initFormToggle(root) {
    var formWrap = root.querySelector('[data-reviews-form-wrap]');
    if (!formWrap) return;
    var toggles = root.querySelectorAll('[data-reviews-toggle-form]');
    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isOpen = !formWrap.hasAttribute('hidden');
        if (isOpen) {
          formWrap.setAttribute('hidden', '');
          toggles.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
        } else {
          formWrap.removeAttribute('hidden');
          toggles.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
          var firstField = formWrap.querySelector('button[data-rating-value], input, textarea');
          if (firstField) firstField.focus();
          formWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });

    var cancel = formWrap.querySelector('[data-reviews-cancel]');
    if (cancel) {
      cancel.addEventListener('click', function () {
        formWrap.setAttribute('hidden', '');
        toggles.forEach(function (b) {
          b.setAttribute('aria-expanded', 'false');
          b.focus();
        });
      });
    }
  }

  // ---------------- Rating picker ----------------
  function initRatingPicker(root) {
    var picker = root.querySelector('[data-review-rating-picker]');
    if (!picker) return;
    var hidden = root.querySelector('[data-review-rating-input]');
    var btns = picker.querySelectorAll('[data-rating-value]');

    function setValue(v) {
      if (hidden) hidden.value = String(v);
      btns.forEach(function (b) {
        var bv = parseInt(b.getAttribute('data-rating-value'), 10);
        var on = bv <= v;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }

    btns.forEach(function (b) {
      b.addEventListener('click', function () {
        setValue(parseInt(b.getAttribute('data-rating-value'), 10));
      });
      b.addEventListener('mouseenter', function () {
        var v = parseInt(b.getAttribute('data-rating-value'), 10);
        btns.forEach(function (x) {
          var xv = parseInt(x.getAttribute('data-rating-value'), 10);
          x.classList.toggle('is-hover', xv <= v);
        });
      });
      b.addEventListener('mouseleave', function () {
        btns.forEach(function (x) { x.classList.remove('is-hover'); });
      });
      b.addEventListener('keydown', function (e) {
        var cur = parseInt(b.getAttribute('data-rating-value'), 10);
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          e.preventDefault();
          var next = Math.min(5, cur + 1);
          setValue(next);
          var nb = picker.querySelector('[data-rating-value="' + next + '"]');
          if (nb) nb.focus();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          e.preventDefault();
          var prev = Math.max(1, cur - 1);
          setValue(prev);
          var pb = picker.querySelector('[data-rating-value="' + prev + '"]');
          if (pb) pb.focus();
        }
      });
    });

    // Validate before submit
    var form = root.querySelector('form.product-reviews__form');
    if (form && hidden) {
      form.addEventListener('submit', function (e) {
        if (!hidden.value) {
          e.preventDefault();
          picker.classList.add('is-invalid');
          var firstBtn = btns[0];
          if (firstBtn) firstBtn.focus();
        }
      });
    }
  }

  // ---------------- Sort / filter ----------------
  function initSort(root) {
    var sel = root.querySelector('[data-reviews-sort]');
    var list = root.querySelector('[data-reviews-list]');
    if (!sel || !list) return;

    var cards = Array.prototype.slice.call(list.querySelectorAll('[data-review]'));
    var originalOrder = cards.slice();

    sel.addEventListener('change', function () {
      var mode = sel.value;
      cards.forEach(function (c) { c.removeAttribute('data-hidden'); });

      var sorted = originalOrder.slice();
      if (mode === 'newest') {
        sorted.sort(function (a, b) { return num(b, 'data-date') - num(a, 'data-date'); });
      } else if (mode === 'highest') {
        sorted.sort(function (a, b) {
          var d = num(b, 'data-rating') - num(a, 'data-rating');
          return d !== 0 ? d : num(b, 'data-date') - num(a, 'data-date');
        });
      } else if (mode === 'lowest') {
        sorted.sort(function (a, b) {
          var d = num(a, 'data-rating') - num(b, 'data-rating');
          return d !== 0 ? d : num(b, 'data-date') - num(a, 'data-date');
        });
      } else if (mode === 'verified') {
        sorted.sort(function (a, b) { return num(b, 'data-date') - num(a, 'data-date'); });
        sorted.forEach(function (c) {
          if (c.getAttribute('data-verified') !== '1') c.setAttribute('data-hidden', '1');
        });
      }
      sorted.forEach(function (c) { list.appendChild(c); });
    });
  }
  function num(el, attr) { return parseInt(el.getAttribute(attr) || '0', 10) || 0; }

  // ---------------- Relative dates ----------------
  function initRelativeDates(root) {
    var nodes = root.querySelectorAll('[data-review-date]');
    var now = Math.floor(Date.now() / 1000);
    nodes.forEach(function (n) {
      var ts = parseInt(n.getAttribute('data-review-date'), 10);
      if (!ts) return;
      var diff = now - ts;
      if (diff < 0) return;
      var day = 86400;
      if (diff < day) {
        n.textContent = 'Today';
      } else if (diff < 2 * day) {
        n.textContent = 'Yesterday';
      } else if (diff < 7 * day) {
        n.textContent = Math.floor(diff / day) + ' days ago';
      } else if (diff < 30 * day) {
        var w = Math.floor(diff / (7 * day));
        n.textContent = w + (w === 1 ? ' week ago' : ' weeks ago');
      }
      // else: keep server-rendered "May 2025"
    });
  }

  // Init all sections on page (handles theme editor reloads)
  function initAll() {
    document.querySelectorAll('.product-reviews').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target && e.target.querySelector ? e.target.querySelector('.product-reviews') : null;
    if (s) init(s);
  });
})();
